// ReportShell — page-level wrapper for report views.
// Renders an in-content header (title + subtitle + breadcrumb + actions),
// followed by stacked sections: filterBar / kpiStrip / children.
//
// IMPORTANT: this lives INSIDE the global app Layout (which provides the
// sidebar + topbar), so we don't render our own sidebar.
import React, { useEffect } from 'react'
import { theme as defaultTheme, T, ensureFonts } from './tokens'
import { Icon } from './icons'

/**
 * Props:
 *   title         : string (h1 within content)
 *   subtitle      : string
 *   breadcrumb    : string[]
 *   accentColor   : optional operator brand color (left bar / pill)
 *   actions       : ReactNode rendered top-right (refresh / export buttons)
 *   filterBar     : ReactNode — first section (FilterBar)
 *   kpiStrip      : ReactNode — second section (KpiStrip)
 *   children      : main content (cards / tables)
 *   density       : 'comfortable' | 'compact' (currently affects padding only)
 *   theme         : design token object (default light)
 */
export default function ReportShell({
  title, subtitle, breadcrumb, accentColor,
  actions, filterBar, kpiStrip, children,
  density = 'comfortable', theme = defaultTheme,
  bleed = true,  // overflow Layout's `p-8` (32px) so the header touches the global topbar
}) {
  useEffect(() => { ensureFonts() }, [])

  const pad = density === 'compact' ? 16 : 24

  return (
    <div style={{
      width: bleed ? 'calc(100% + 64px)' : '100%',
      minHeight: '100%',
      background: theme.bg, color: theme.text,
      fontFamily: T.fontUI,
      letterSpacing: -0.1,
      margin: bleed ? '-32px' : 0,
    }}>
      {/* Header — sticky-feel, on surface */}
      <header style={{
        padding: density === 'compact' ? '14px 20px' : '18px 24px',
        background: theme.surface,
        borderBottom: `1px solid ${theme.border}`,
        display: 'flex', alignItems: 'center', gap: 16,
        position: 'relative',
      }}>
        {/* operator accent strip */}
        {accentColor && (
          <span style={{
            position: 'absolute', left: 0, top: 0, bottom: 0, width: 3,
            background: accentColor,
          }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          {breadcrumb && breadcrumb.length > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 11, color: theme.textDim, marginBottom: 4,
            }}>
              {breadcrumb.map((b, i) => (
                <React.Fragment key={i}>
                  <span style={{
                    color: i === breadcrumb.length - 1 ? theme.textSub : theme.textDim,
                  }}>{b}</span>
                  {i < breadcrumb.length - 1 && <span style={{ color: theme.textDim }}>/</span>}
                </React.Fragment>
              ))}
            </div>
          )}
          <div style={{
            fontSize: 19, fontWeight: 700, color: theme.text,
            letterSpacing: -0.3, lineHeight: 1.15,
          }}>{title}</div>
          {subtitle && (
            <div style={{
              fontSize: 12.5, color: theme.textSub,
              marginTop: 3, lineHeight: 1.4,
            }}>{subtitle}</div>
          )}
        </div>
        {actions && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
            {actions}
          </div>
        )}
      </header>

      {/* Body */}
      <div style={{
        padding: pad,
        display: 'flex', flexDirection: 'column', gap: pad,
      }}>
        {filterBar}
        {kpiStrip}
        {children}
        <div style={{ height: 16 }} />
      </div>
    </div>
  )
}

// Header action buttons that match the design.
export function HeaderButton({ onClick, disabled, primary = false, children, theme = defaultTheme, title }) {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 14px', borderRadius: 7,
    fontSize: 12.5, fontWeight: primary ? 600 : 500,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: T.fontUI,
    border: 'none',
    opacity: disabled ? 0.55 : 1,
    transition: 'opacity 120ms',
    whiteSpace: 'nowrap',
  }
  const variantStyle = primary
    ? { background: theme.accent, color: '#fff' }
    : { background: theme.surface, color: theme.text, border: `1px solid ${theme.border}` }
  return (
    <button onClick={onClick} disabled={disabled} title={title}
      style={{ ...base, ...variantStyle }}>
      {children}
    </button>
  )
}

export { Icon }
